import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Zap, Heart } from "lucide-react";
import { taka } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { toast } from "sonner";

export const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { toggle, isWished } = useWishlist();
  const navigate = useNavigate();
  const low = product.stock <= (product.min_stock || 0) && product.stock > 0;
  const out = product.stock <= 0;
  const discount = product.retail_price && product.price < product.retail_price
    ? Math.round((1 - product.price / product.retail_price) * 100) : 0;

  const add = (e) => {
    e.preventDefault();
    if (out) return;
    addItem(product, 1);
    toast.success(`${product.name_bn} কার্টে যোগ হয়েছে`);
  };

  const onHeart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const r = await toggle(product.id);
    if (r.needLogin) { toast.error("উইশলিস্টের জন্য লগইন করুন"); navigate("/login"); return; }
    toast.success(r.added ? "উইশলিস্টে যোগ হয়েছে" : "উইশলিস্ট থেকে সরানো হয়েছে");
  };
  const wished = isWished(product.id);

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group bg-white rounded-2xl border border-stone-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden flex flex-col"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
        <img
          src={product.image || undefined}
          alt={product.name_bn}
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=600"; }}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        {discount > 0 && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-md">
            -{discount}%
          </span>
        )}
        {product.featured && (
          <span className="absolute top-2 right-2 bg-[#CA8A04] text-white text-[10px] font-bold px-2 py-0.5 rounded-md">
            ফিচার্ড
          </span>
        )}
        <button
          onClick={onHeart}
          data-testid={`wishlist-${product.id}`}
          className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur bg-white/80 shadow-sm transition-colors ${wished ? "text-red-500" : "text-stone-500"}`}
        >
          <Heart size={16} fill={wished ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="p-3 sm:p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-[#1C1917] text-sm sm:text-base leading-snug line-clamp-2">
          {product.name_bn}
        </h3>
        <div className="mt-1 flex items-center gap-2">
          <span className="font-bold text-emerald-800 text-lg">{taka(product.price)}</span>
          <span className="text-xs text-stone-500">/ {product.unit}</span>
        </div>
        <div className="mt-2">
          {out ? (
            <span className="bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-1 rounded-full">স্টক নেই</span>
          ) : low ? (
            <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-1 rounded-full">
              স্টক কম ({Math.round(product.stock)})
            </span>
          ) : (
            <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-2.5 py-1 rounded-full">
              স্টক আছে
            </span>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            onClick={add}
            disabled={out}
            data-testid={`add-to-cart-${product.id}`}
            className="flex items-center justify-center gap-1 bg-[#1E3A2B] hover:bg-[#14281D] disabled:opacity-40 text-white text-xs font-medium py-2.5 rounded-xl transition-colors active:scale-95"
          >
            <ShoppingCart size={15} /> কার্ট
          </button>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (out) return; addItem(product, 1); navigate("/cart"); }}
            disabled={out}
            data-testid={`buy-now-${product.id}`}
            className="flex items-center justify-center gap-1 bg-[#CA8A04] hover:bg-[#A16207] disabled:opacity-40 text-white text-xs font-medium py-2.5 rounded-xl transition-colors active:scale-95"
          >
            <Zap size={15} /> কিনুন
          </button>
        </div>
      </div>
    </Link>
  );
};
